import { c as _c } from "react/compiler-runtime";
import { Mail, ChevronLeft } from "lucide-react";
import { motion } from "framer-motion";
import { MutableRefObject, KeyboardEvent, FormEvent } from "react";
import type { ThemeClasses } from "./NewsletterModal";
interface VerifyEmailStepProps {
  email: string;
  otp: string[];
  otpError: boolean;
  otpErrorMessage: string;
  resendCooldown: number;
  otpSendError: string;
  isVerifying: boolean;
  otpInputsRef: MutableRefObject<(HTMLInputElement | null)[]>;
  onBack: () => void;
  onSubmit: (e: FormEvent) => void;
  onOtpChange: (index: number, value: string) => void;
  onKeyDown: (index: number, e: KeyboardEvent<HTMLInputElement>) => void;
  onResend: () => void;
  theme: ThemeClasses;
}
export function VerifyEmailStep(t0) {
  const $ = _c(60);
  const {
    email,
    otp,
    otpError,
    otpErrorMessage,
    resendCooldown,
    otpSendError,
    isVerifying,
    otpInputsRef,
    onBack,
    onSubmit,
    onOtpChange,
    onKeyDown,
    onResend,
    theme
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0,
      scale: 0.95
    };
    t2 = {
      opacity: 1,
      scale: 1
    };
    t3 = {
      opacity: 0,
      scale: 1.05
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ChevronLeft className="w-5 h-5" />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== onBack) {
    t5 = <button onClick={onBack} className="absolute left-0 top-0 p-2 text-gray-400 hover:text-gray-900 transition-colors rounded-full hover:bg-gray-100" aria-label="Back to interests">{t4}</button>;
    $[4] = onBack;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = `w-12 h-12 sm:w-16 sm:h-16 ${theme.bgLight10} rounded-2xl flex items-center justify-center`;
  const t7 = `w-6 h-6 sm:w-8 sm:h-8 ${theme.text}`;
  let t8;
  if ($[6] !== t7) {
    t8 = <Mail className={t7} />;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== t6 || $[9] !== t8) {
    t9 = <div className="flex justify-center mb-4 sm:mb-6"><div className={t6}>{t8}</div></div>;
    $[8] = t6;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const t10 = `text-lg sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3 ${theme.font}`;
  let t11;
  if ($[11] !== t10) {
    t11 = <h3 className={t10}>Please check your email</h3>;
    $[11] = t10;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const t12 = `text-xs sm:text-base text-gray-600 mb-4 sm:mb-8 ${theme.font}`;
  let t13;
  if ($[13] !== email) {
    t13 = <strong className="text-gray-900">{email}</strong>;
    $[13] = email;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== t12 || $[16] !== t13) {
    t14 = <p className={t12}>We've sent a code to{" "}{t13}</p>;
    $[15] = t12;
    $[16] = t13;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== onKeyDown || $[19] !== onOtpChange || $[20] !== otp || $[21] !== otpError || $[22] !== otpInputsRef || $[23] !== theme.borderFocus || $[24] !== theme.font) {
    t15 = otp.map((digit, idx) => <input key={idx} ref={el => {
      otpInputsRef.current[idx] = el;
    }} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => onOtpChange(idx, e.target.value)} onKeyDown={e_0 => onKeyDown(idx, e_0)} className={`w-9 h-11 sm:w-14 sm:h-14 text-center text-xl sm:text-2xl font-semibold border-2 rounded-lg focus:outline-none transition-all bg-white ${theme.font} text-gray-900 ${otpError ? "border-red-500" : `border-gray-300 ${theme.borderFocus}`}`} />);
    $[18] = onKeyDown;
    $[19] = onOtpChange;
    $[20] = otp;
    $[21] = otpError;
    $[22] = otpInputsRef;
    $[23] = theme.borderFocus;
    $[24] = theme.font;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t15) {
    t16 = <div className="flex justify-center gap-1.5 sm:gap-2 mb-4 sm:mb-6 flex-wrap">{t15}</div>;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== otpSendError || $[29] !== theme.font) {
    t17 = otpSendError && <p className={`text-sm sm:text-base text-red-500 mt-2 mb-4 ${theme.font}`}>{otpSendError}</p>;
    $[28] = otpSendError;
    $[29] = theme.font;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== otpError || $[32] !== otpErrorMessage || $[33] !== theme.font) {
    t18 = otpError && <p className={`text-sm sm:text-base text-red-500 mt-2 mb-4 ${theme.font}`}>{otpErrorMessage || "Invalid verification code. Please try again."}</p>;
    $[31] = otpError;
    $[32] = otpErrorMessage;
    $[33] = theme.font;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== isVerifying || $[36] !== otp) {
    t19 = otp.some(_temp) || isVerifying;
    $[35] = isVerifying;
    $[36] = otp;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const t20 = `w-full px-4 sm:px-6 py-3 sm:py-3.5 rounded-lg font-medium text-sm sm:text-base mb-3 sm:mb-4 transition-colors ${theme.font} ${otp.some(_temp2) || isVerifying ? "bg-gray-200 text-gray-400 cursor-not-allowed" : `${theme.bg} text-white ${theme.hoverBg}`}`;
  const t21 = isVerifying ? "Verifying..." : "Verify";
  let t22;
  if ($[38] !== t19 || $[39] !== t20 || $[40] !== t21) {
    t22 = <button type="submit" disabled={t19} className={t20}>{t21}</button>;
    $[38] = t19;
    $[39] = t20;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== onResend || $[43] !== resendCooldown || $[44] !== theme.font || $[45] !== theme.hoverText) {
    t23 = resendCooldown > 0 ? <p className={`text-xs sm:text-sm text-gray-600 ${theme.font}`}>You can request a new code in{" "}<span className="font-semibold text-gray-900">{resendCooldown}s</span>.</p> : <p className={`text-xs sm:text-sm text-gray-600 ${theme.font}`}>Didn't receive an email?{" "}<button type="button" onClick={onResend} className={`text-gray-900 font-semibold ${theme.hoverText} transition-colors`}>Resend</button></p>;
    $[42] = onResend;
    $[43] = resendCooldown;
    $[44] = theme.font;
    $[45] = theme.hoverText;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== onSubmit || $[48] !== t16 || $[49] !== t17 || $[50] !== t18 || $[51] !== t22 || $[52] !== t23) {
    t24 = <form onSubmit={onSubmit}>{t16}{t17}{t18}{t22}{t23}</form>;
    $[47] = onSubmit;
    $[48] = t16;
    $[49] = t17;
    $[50] = t18;
    $[51] = t22;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t11 || $[55] !== t14 || $[56] !== t24 || $[57] !== t5 || $[58] !== t9) {
    t25 = <motion.div key="verification" initial={t1} animate={t2} exit={t3} className="text-center py-2 sm:py-4 relative">{t5}{t9}{t11}{t14}{t24}</motion.div>;
    $[54] = t11;
    $[55] = t14;
    $[56] = t24;
    $[57] = t5;
    $[58] = t9;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  return t25;
}
function _temp2(d_0) {
  return !d_0;
}
function _temp(d) {
  return !d;
}